import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { format, formatDistanceToNow } from 'date-fns'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function formatTimestamp(timestamp: number): string {
  return format(new Date(timestamp * 1000), 'MMM d, yyyy h:mm a')
}

export function formatRelativeTime(timestamp: number): string {
  return formatDistanceToNow(new Date(timestamp * 1000), { addSuffix: true })
}

export function formatDuration(ms: number): string {
  if (ms < 1000) {
    return `${ms}ms`
  }

  const seconds = Math.floor(ms / 1000)
  if (seconds < 60) {
    return `${seconds}s`
  }

  const minutes = Math.floor(seconds / 60)
  const remainingSeconds = seconds % 60

  if (minutes < 60) {
    return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`
  }

  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60

  return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`
}

export function getStatusColor(status: string): string {
  switch (status) {
    case 'success':
    case 'completed':
    case 'active':
      return 'status-success'
    case 'failed':
    case 'error':
      return 'status-error'
    case 'running':
    case 'queued':
      return 'status-info'
    case 'cancelled':
    case 'paused':
      return 'status-neutral'
    case 'archived':
      return 'status-warning'
    default:
      return 'status-neutral'
  }
}

export function getStatusVariant(status: string): "default" | "secondary" | "destructive" | "outline" {
  switch (status) {
    case 'success':
    case 'completed':
    case 'active':
      return 'default'
    case 'failed':
    case 'error':
      return 'destructive'
    case 'running':
    case 'queued':
      return 'secondary'
    case 'cancelled':
    case 'paused':
    case 'archived':
      return 'outline'
    default:
      return 'outline'
  }
}

export function getStatusIcon(status: string): string {
  switch (status) {
    case 'success':
    case 'completed':
      return '✓'
    case 'failed':
    case 'error':
      return '✗'
    case 'running':
      return '⟳'
    case 'queued':
      return '⏳'
    case 'cancelled':
      return '⏹'
    case 'paused':
      return '⏸'
    case 'archived':
      return '📁'
    default:
      return '?'
  }
}

export function validateUrl(url: string): boolean {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

export function validateCronExpression(expression: string): boolean {
  // Basic cron validation - 5 or 6 parts separated by spaces
  const parts = expression.trim().split(/\s+/);
  if (parts.length !== 5 && parts.length !== 6) {
    return false;
  }

  // Basic validation for each part
  const cronRegex = [
    /^(\*|([0-5]?\d)(,([0-5]?\d))*|([0-5]?\d)-([0-5]?\d))$/, // minute
    /^(\*|([01]?\d|2[0-3])(,([01]?\d|2[0-3]))*|([01]?\d|2[0-3])-([01]?\d|2[0-3]))$/, // hour
    /^(\*|([12]?\d|3[01])(,([12]?\d|3[01]))*|([12]?\d|3[01])-([12]?\d|3[01]))$/, // day
    /^(\*|([1-9]|1[0-2])(,([1-9]|1[0-2]))*|([1-9]|1[0-2])-([1-9]|1[0-2]))$/, // month
    /^(\*|[0-6](,[0-6])*|[0-6]-[0-6])$/, // day of week
  ];

  // Validate first 5 parts (minute, hour, day, month, day of week)
  for (let i = 0; i < 5; i++) {
    if (!cronRegex[i].test(parts[i])) {
      return false;
    }
  }

  return true;
}

export function generateId(): string {
  return Math.random().toString(36).substring(2) + Date.now().toString(36);
}
